import {
  ArticleProps,
  ArticleComponent,
} from '@hyphengroup/cms-components/components/Article';
import { FeLink } from '@hyphengroup/cms-components/components/FeLink';
import { MaterialIcon } from '@hyphengroup/cms-components/components/MaterialIcon';
import { TheWrapper } from '@hyphengroup/cms-components/components/TheWrapper';
import { MaterialIconName } from '@hyphengroup/cms-components/types/materialIconName';
import { RelevantArticle as RelevantArticleItem } from '@hyphengroup/cms-components/types/RelevantArticle';
import {
  getInternalArticleUrl,
  resolveRelevantArticleCard,
} from '@hyphengroup/cms-components/utils/relevantArticleLinks';
import { getClientTranslations } from '@hyphengroup/cms-components/utils/translations/getClientTranslations';
import { staticText } from '@hyphengroup/cms-components/utils/translations/staticText';
import classNames from 'classnames';
import { pushDataLayer } from '../pushDataLayer';
import {
  ComponentRelevantArticle,
  EnumRelevantArticleGroupType,
  EnumRelevantArticleMode,
  EnumTypeReleventArticle,
} from '$/(cms)/_modules/cmsService/types';
import { useClientContext } from '$/modules/context/client';

const TRANSLATIONS = staticText({
  relevant_article_group_compare_products: {
    _: 'Compare Products',
    zh: '即時比較',
  },
  relevant_article_group_learn_more: {
    _: 'Learn More',
    zh: '實用攻略',
  },
  relevant_article_group_related_guides: {
    _: 'Trending Now',
    zh: '近期熱門',
  },
  relevant_article_keep_exploring_title: {
    _: 'Keep Exploring',
    zh: '延伸閱讀',
  },
});

type GroupTranslationKey =
  | 'relevant_article_group_compare_products'
  | 'relevant_article_group_learn_more'
  | 'relevant_article_group_related_guides';

// Fixed render order regardless of Strapi config order.
const GROUP_CONFIG: ReadonlyArray<{
  type: EnumRelevantArticleGroupType;
  icon: MaterialIconName;
  labelKey: GroupTranslationKey;
}> = [
  {
    type: EnumRelevantArticleGroupType.CompareProducts,
    icon: 'balance',
    labelKey: 'relevant_article_group_compare_products',
  },
  {
    type: EnumRelevantArticleGroupType.LearnMore,
    icon: 'menu_book',
    labelKey: 'relevant_article_group_learn_more',
  },
  {
    type: EnumRelevantArticleGroupType.RelatedGuides,
    icon: 'crown',
    labelKey: 'relevant_article_group_related_guides',
  },
];

const MAX_LINKS_PER_GROUP = 3;

type GroupedLink = { url: string; label: string };

const RelevantArticleGrouped: React.FC<
  Pick<ComponentRelevantArticle, 'articles' | 'title' | 'id'> & {
    componentIndex?: string;
  }
> = ({ articles, title, id, componentIndex }) => {
  const { brand, langPrefix, lang } = useClientContext();
  const t = getClientTranslations(TRANSLATIONS, { brand, lang });

  const sectionTitle = title?.trim() || t.relevant_article_keep_exploring_title;

  const buildLink = (art: RelevantArticleItem): GroupedLink | undefined => {
    const manualLabel = art.link_label?.trim();

    if (art.article) {
      const url = getInternalArticleUrl(art.article, 'article', {
        brand,
        langPrefix,
      });
      const label = manualLabel || art.article.title || '';
      return url && label ? { url, label } : undefined;
    }

    if (art.page) {
      const url = getInternalArticleUrl(art.page, 'page', {
        brand,
        langPrefix,
      });
      const label = manualLabel || art.page.title || '';
      return url && label ? { url, label } : undefined;
    }

    // Grouped navigation supports internal links only.
    return undefined;
  };

  const groups = GROUP_CONFIG.map((config) => {
    const links = (articles ?? [])
      .filter((art) => art?.group_type === config.type)
      .map((art) => buildLink(art))
      .filter((link): link is GroupedLink => link !== undefined)
      .slice(0, MAX_LINKS_PER_GROUP);

    return { ...config, links };
  }).filter((group) => group.links.length > 0);

  if (groups.length === 0) {
    return null;
  }

  return (
    <div className="mx-auto max-w-screen-2xl">
      <TheWrapper type="simple" id={id}>
        <h3 className="mhg-alpha-display-1 pb-spacing/gap/y">{sectionTitle}</h3>
        <div className="flex flex-col gap-8 rounded-radius/card border border-borders/default py-spacing/gap/x bg-fill/card">
          {groups.map((group, groupIndex) => {
            // Continuous position across the whole rendered column (0..N),
            // not per-group, so `index` reflects position on the page.
            const linkIndexOffset = groups
              .slice(0, groupIndex)
              .reduce((sum, prevGroup) => sum + prevGroup.links.length, 0);

            return (
              <div
                key={group.type}
                className="flex flex-col gap-spacing/padding/y-sm"
              >
                <div className="flex items-center gap-spacing/gap/soft-12px px-spacing/padding/x">
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-fill/primary-light text-text/accent">
                    <MaterialIcon icon={group.icon} />
                  </span>
                  <span className="mhg-alpha-subheading-2 min-w-0 flex-1 truncate text-text/primary">
                    {t[group.labelKey]}
                  </span>
                </div>
                <div className="flex flex-col gap-spacing/gap/soft-12px px-spacing/padding/x">
                  {group.links.map((link, index) => (
                    <FeLink
                      key={[group.type, link.url, index].join('_')}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={link.label}
                      className="flex items-center gap-spacing/gap/soft-12px rounded-radius/button p-spacing/gap/soft-12px bg-fill/alternative-1"
                      onClick={() =>
                        pushDataLayer({
                          event: 'navigate',
                          component_type: 'article-list',
                          component_title: `${sectionTitle}/${t[group.labelKey]}_${link.label}`,
                          component_index: componentIndex ?? '',
                          action: 'click',
                          index: String(linkIndexOffset + index),
                        })
                      }
                    >
                      <span className="mhg-alpha-button-2 min-w-0 flex-1 truncate font-semibold text-black">
                        {link.label}
                      </span>
                      <MaterialIcon
                        icon="arrow_right_alt"
                        className="shrink-0"
                      />
                    </FeLink>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </TheWrapper>
    </div>
  );
};

export const RelevantArticle: React.FC<
  ComponentRelevantArticle & { componentIndex?: string }
> = ({ articles, title, id, type, mode, componentIndex }) => {
  const { brand, langPrefix } = useClientContext();

  if (!articles || articles.length === 0) {
    return null;
  }

  if (mode === EnumRelevantArticleMode.GroupedNavigation) {
    return (
      <RelevantArticleGrouped
        articles={articles}
        title={title}
        id={id}
        componentIndex={componentIndex}
      />
    );
  }

  const relevantArticles: Omit<ArticleProps, 'pushDataLayer'>[] = articles
    .map((art) => resolveRelevantArticleCard(art, { brand, langPrefix }))
    .filter((x) => x !== undefined);

  return (
    <div className="mx-auto max-w-screen-2xl">
      <TheWrapper type="simple" title={title ?? ''} id={id}>
        {type === EnumTypeReleventArticle.Big ? (
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2 lg:gap-6">
            <div>
              <div className="mb-4 w-full rounded-2xl shadow bg-white md:rounded-3xl">
                <ArticleComponent
                  {...relevantArticles?.[0]}
                  pushDataLayer={pushDataLayer}
                />
              </div>
            </div>
            <div className="items-start">
              {relevantArticles?.slice(1, 4).map((article, index) => {
                const articleKey = [
                  'relevant-article',
                  article.url ?? '',
                  article.title ?? '',
                  index,
                ]
                  .filter(Boolean)
                  .join('_');

                return (
                  <div
                    key={articleKey}
                    className={classNames(
                      'w-full rounded-2xl shadow bg-white md:rounded-3xl',
                      {
                        'mb-4 2xl:mb-4': index + 1 !== relevantArticles.length,
                      },
                    )}
                  >
                    <ArticleComponent
                      subtitle={article.subtitle}
                      title={article.title}
                      url={article.url}
                      pushDataLayer={pushDataLayer}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div
            className={classNames('grid gap-spacing/gap/x', {
              'grid-cols-1 md:grid-cols-2': relevantArticles?.length === 1,
              'grid-cols-2':
                relevantArticles?.length === 2 ||
                (relevantArticles?.length || 0) > 3,
              'grid-cols-2 md:grid-cols-3': relevantArticles?.length === 3,
            })}
          >
            {relevantArticles?.map((article, index) => {
              const articleKey = [
                'relevant-article',
                article.url ?? '',
                article.title ?? '',
                index,
              ]
                .filter(Boolean)
                .join('_');

              return (
                <div
                  key={articleKey}
                  className="w-full rounded-radius/card border border-borders/default bg-fill/card"
                >
                  <ArticleComponent
                    subtitle={article.subtitle}
                    title={article.title}
                    url={article.url}
                    pushDataLayer={pushDataLayer}
                  />
                </div>
              );
            })}
          </div>
        )}
      </TheWrapper>
    </div>
  );
};
